import { Table, TableBody, Tooltip, Typography } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import Collapse from '@material-ui/core/Collapse'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import { Visibility } from '@material-ui/icons'
import AssignmentReturnedOutlinedIcon from '@material-ui/icons/AssignmentReturnedOutlined'
import BlockOutlinedIcon from '@material-ui/icons/BlockOutlined'
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircleOutlined'
import QueryBuilderOutlinedIcon from '@material-ui/icons/QueryBuilderOutlined'
import clsx from 'clsx'
import { Link } from 'react-router-dom'

import { InterestLayers, layersMap } from '@/constants/diagnoses'
import { DiagnosisDTO } from '@/hooks/queries/use-get-physiological-diagnoses'
import { useCompanies } from '@/hooks/use-companies'
import { routes } from '@/routes/config'

import { useStyles } from './styles'

interface RowProps {
  row: DiagnosisDTO
  isOpen: boolean
  onRowClick: () => void
}

const tagKeys = {
  cicle: (row: DiagnosisDTO) => row.formattedCicle,
  phenological_stage: (row: DiagnosisDTO) => row.phenological_stage,
  state_acr: (row: DiagnosisDTO) => row.state_acr,
}

export function Row({ row, isOpen, onRowClick }: RowProps) {
  const classes = useStyles()
  const { selectedCompany } = useCompanies()

  const STATUS_MAP = {
    registered: {
      color: classes.blueLabel,
      icon: <AssignmentReturnedOutlinedIcon />,
    },
    completed: {
      color: classes.greenLabel,
      icon: <CheckCircleOutlinedIcon />,
    },
    processing: {
      color: classes.orangeLabel,
      icon: <QueryBuilderOutlinedIcon />,
    },
    rejected: {
      color: classes.redLabel,
      icon: <BlockOutlinedIcon />,
    },
  }

  const statusMap = STATUS_MAP[row.status.status_key as keyof typeof STATUS_MAP]

  const linkDisabled = row.status.status_key !== 'completed'

  function addLineBreakInDescription() {
    const [firstPart, ...rest] = row.status.description.split('.')
    return (
      <>
        {firstPart}.<br />
        {rest.join('.')}
      </>
    )
  }

  return (
    <>
      <TableRow
        className={clsx(classes.root, {
          [classes.disabled]: linkDisabled,
        })}
        onClick={onRowClick}
        aria-label='expand row'
      >
        <TableCell>
          <Typography variant='subtitle1'>{row.formattedDate}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant='subtitle1'>{row.project.name}</Typography>
        </TableCell>
        <TableCell>
          <div className={classes.tags}>
            {Object.keys(tagKeys).map(
              (key) =>
                row[key as keyof DiagnosisDTO] && (
                  <span key={key}>
                    {tagKeys[key as keyof typeof tagKeys](row)}
                  </span>
                ),
            )}
          </div>
        </TableCell>
        <Tooltip
          title={addLineBreakInDescription()}
          placement='bottom-end'
          classes={{
            tooltip: classes.tooltip,
            popper: classes.popper,
          }}
        >
          <TableCell className={classes.statusCell}>
            <div className={statusMap.color}>
              <span>{statusMap.icon}</span>
              {row.status.label}
            </div>
          </TableCell>
        </Tooltip>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6}>
          <Collapse in={isOpen} timeout='auto' unmountOnExit>
            <Box>
              <Table className={classes.tableItem}>
                <TableBody>
                  {Object.keys(layersMap)
                    .filter(
                      (layer) =>
                        row.summary &&
                        row.summary.layers.includes(layer as InterestLayers),
                    )
                    .map((key) => (
                      <TableRow key={key}>
                        <TableCell className={classes.cellLayers}>
                          <Link
                            to={
                              routes.DiagnosisView(
                                selectedCompany.id.toString(),
                                row.culture,
                                'physiology',
                                row.id,
                              ) + `?layer=${key}`
                            }
                          >
                            <div>
                              {layersMap[key as InterestLayers].icon}
                              <Typography variant='h3'>
                                {layersMap[key as InterestLayers].message}
                              </Typography>
                              <Visibility />
                            </div>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}
